import { useEffect, useState } from "react";
import { api, onApiError, type MeResponse } from "./api";
import { Login } from "./pages/Login";
import { Job } from "./pages/Job";
import { History } from "./pages/History";
import { AdminApprove } from "./pages/AdminApprove";
import { RequestSubmitted } from "./pages/RequestSubmitted";
import { MeetingNew } from "./pages/MeetingNew";
import { Meeting } from "./pages/Meeting";
import { Meetings } from "./pages/Meetings";
import { Transcript } from "./pages/Transcript";
import { Frames } from "./pages/Frames";
import { Settings } from "./pages/Settings";
import { EdgeState } from "./pages/EdgeState";
import { Design } from "./pages/Design";
import { useLocale, useT, type Locale } from "./i18n/useT";
import { navigate, routes, useRoute } from "./router";
import { AppShell } from "./components/design/AppShell";

type Auth =
  | { kind: "loading" }
  | { kind: "logged_out" }
  | { kind: "logged_in"; me: MeResponse };

const PENDING_DEST_KEY = "recspec.pendingDest";

export function App() {
  const [auth, setAuth] = useState<Auth>({ kind: "loading" });
  const route = useRoute();

  useEffect(() => {
    api
      .me()
      .then((me) => setAuth({ kind: "logged_in", me }))
      .catch(() => setAuth({ kind: "logged_out" }));
  }, []);

  // Global api-error → edge-route mapping. Specific error codes from the
  // api signal a workspace-level edge state (bot couldn't join Meet,
  // Recall vendor is unreachable, finalize blew up). The corresponding
  // /edge/<kind> screen explains the state and offers actions. The
  // listener fires _in addition_ to whatever the original caller's
  // .catch() does — local recovery still works.
  useEffect(() => {
    return onApiError((err) => {
      switch (err.code) {
        case "meeting_bot_unavailable":
        case "recall_rejected":
          navigate(routes.edge("recall_down"));
          return;
        case "bot_blocked":
        case "bot_blocked_by_host":
          navigate(routes.edge("bot_blocked"));
          return;
        case "missing_calendar_permissions":
        case "calendar_permission_denied":
          navigate(routes.edge("missing_calendar_permissions"));
          return;
        default:
          // No edge mapping — let the caller handle it locally.
          return;
      }
    });
  }, []);

  // Network drop → /edge/offline after 5 s. Reconnect lives inside the
  // EdgeState component itself (polls navigator.onLine and navigates
  // back to home on recovery). Skip the redirect when we're already on
  // the offline screen; suppress on public routes too so the post-OAuth
  // landing doesn't flash through an offline state.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (route.kind === "edge" && route.errorKind === "offline") return;
    let pending: number | null = null;
    const arm = () => {
      if (pending !== null) return;
      pending = window.setTimeout(() => {
        if (!window.navigator.onLine) navigate(routes.edge("offline"));
      }, 5000);
    };
    const disarm = () => {
      if (pending !== null) {
        window.clearTimeout(pending);
        pending = null;
      }
    };
    if (!window.navigator.onLine) arm();
    window.addEventListener("offline", arm);
    window.addEventListener("online", disarm);
    return () => {
      disarm();
      window.removeEventListener("offline", arm);
      window.removeEventListener("online", disarm);
    };
  }, [route]);

  // After successful auth, replay any URL the user originally requested
  // before being bounced to Login. Stored in sessionStorage by the
  // logged-out branch below.
  useEffect(() => {
    if (auth.kind !== "logged_in") return;
    const dest = sessionStorage.getItem(PENDING_DEST_KEY);
    if (!dest) return;
    sessionStorage.removeItem(PENDING_DEST_KEY);
    if (dest !== window.location.pathname + window.location.search) {
      navigate(dest, /*replace*/ true);
    }
  }, [auth.kind]);

  // Public routes: render regardless of auth state. The admin approval
  // page authenticates via the JWT in the URL, not a session cookie.
  // The pending / request-submitted pages are post-OAuth-failure
  // landings — the user has just bounced off the allowlist gate.
  const isPublicRoute =
    route.kind === "admin_approve" ||
    route.kind === "request_submitted" ||
    route.kind === "pending" ||
    route.kind === "design";

  // Public routes render outside the shell so the login/onboarding flow
  // doesn't show the sidebar (and doesn't depend on auth state).
  if (isPublicRoute) {
    return (
      <div className="app">
        <PublicRoute route={route} />
      </div>
    );
  }

  if (auth.kind === "loading") {
    return (
      <div className="app">
        <p className="muted">Loading…</p>
      </div>
    );
  }

  if (auth.kind === "logged_out") {
    return (
      <div className="app">
        <LoggedOutGate route={route} />
      </div>
    );
  }

  return (
    <AppShell
      sidebarFooter={
        <SidebarFooter
          me={auth.me}
          onLogout={async () => {
            await api.logout();
            setAuth({ kind: "logged_out" });
            navigate(routes.home(), /*replace*/ true);
          }}
        />
      }
    >
      <LoggedIn route={route} />
    </AppShell>
  );
}

function PublicRoute({ route }: { route: ReturnType<typeof useRoute> }) {
  if (route.kind === "admin_approve") {
    return <AdminApprove token={route.token} />;
  }
  if (route.kind === "request_submitted") {
    return <RequestSubmitted />;
  }
  if (route.kind === "pending") {
    return <RequestSubmitted pending />;
  }
  if (route.kind === "design") {
    return <Design />;
  }
  return null;
}

function LoggedOutGate({ route }: { route: ReturnType<typeof useRoute> }) {
  // Remember any non-home destination so we can replay after auth.
  useEffect(() => {
    if (route.kind !== "home") {
      sessionStorage.setItem(
        PENDING_DEST_KEY,
        window.location.pathname + window.location.search,
      );
    }
  }, [route]);
  return <Login />;
}

function LoggedIn({ route }: { route: ReturnType<typeof useRoute> }) {
  if (route.kind === "history") {
    return <History />;
  }
  if (route.kind === "job") {
    return (
      <Job
        jobId={route.jobId}
        tab={route.tab}
        onAnother={() => navigate(routes.home())}
      />
    );
  }
  if (route.kind === "meeting_new") {
    return <MeetingNew />;
  }
  if (route.kind === "meetings_list") {
    return <Meetings />;
  }
  if (route.kind === "meeting") {
    return <Meeting meetingId={route.meetingId} />;
  }
  if (route.kind === "transcript") {
    return <Transcript meetingId={route.meetingId} />;
  }
  if (route.kind === "frames") {
    return <Frames meetingId={route.meetingId} />;
  }
  if (route.kind === "settings") {
    return <Settings section={route.section} />;
  }
  if (route.kind === "edge") {
    return (
      <EdgeState errorKind={route.errorKind} meetingId={route.meetingId} />
    );
  }
  return <Meetings />;
}

/// Post-login landing. Shows three uniform action cards (upload a recording,
function SidebarFooter({
  me,
  onLogout,
}: {
  me: MeResponse;
  onLogout: () => void;
}) {
  const t = useT();
  const who =
    me.auth_method === "cookie"
      ? `@${me.identity.gh_login}`
      : me.auth_method === "bearer_api_key"
        ? me.identity.uid
        : me.identity.email;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <LocaleSelector />
      <div className="dim" style={{ fontSize: 11.5 }}>
        {who}
      </div>
      <button
        type="button"
        className="btn sm"
        onClick={onLogout}
        style={{ alignSelf: "flex-start" }}
      >
        {t("nav.signOut")}
      </button>
    </div>
  );
}

function LocaleSelector() {
  const { locale, setLocale } = useLocale();
  const opts: Locale[] = ["EN", "PT", "DE"];
  return (
    <div role="radiogroup" aria-label="Locale" style={{ display: "flex", gap: 4 }}>
      {opts.map((code) => (
        <button
          key={code}
          type="button"
          onClick={() => setLocale(code)}
          aria-pressed={locale === code}
          className="secondary"
          style={{
            padding: "2px 8px",
            fontSize: 12,
            opacity: locale === code ? 1 : 0.6,
            border:
              locale === code
                ? "1px solid var(--accent, #5b8def)"
                : "1px solid var(--border, #ddd)",
          }}
        >
          {code}
        </button>
      ))}
    </div>
  );
}
